import { useEffect, useRef, useState } from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import AuditPopup from "@/components/AuditPopup";
import AuditAnnouncement from "@/components/AuditAnnouncement";
import WhatsappHero from "@/components/whatsapp/WhatsappHero";
import { WhatsappProblem, WhatsappFeatures, WhatsappProcess, WhatsappWhy } from "@/components/whatsapp/WhatsappSections";
import WhatsappPricing from "@/components/whatsapp/WhatsappPricing";
import WhatsappIndustries from "@/components/whatsapp/WhatsappIndustries";
import WhatsappDemo from "@/components/whatsapp/WhatsappDemo";
import { WhatsappFAQ, WA_FAQS } from "@/components/whatsapp/WhatsappFaqCta";
import WhatsappForm from "@/components/whatsapp/WhatsappForm";
import SectionRail from "@/components/sections/SectionRail";
import ReadingProgress from "@/components/sections/ReadingProgress";
import { trackEvent } from "@/lib/tracking";
import { usePageMeta } from "@/lib/meta";
import { useHashScroll } from "@/lib/useHashScroll";
import MobileStickyCta from "@/components/MobileStickyCta";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "serviceType": "WhatsApp Automation",
      "name": "WhatsApp Automation",
      "areaServed": { "@type": "Country", "name": "South Africa" },
      "provider": { "@type": "LocalBusiness", "name": "Rekisa", "telephone": "+27714679642", "email": "thato@rekisa.co.za" },
      "description": "Rekisa provides WhatsApp automation setup and management for South African businesses using Chatfuel. The service helps businesses automate lead capture, customer support, FAQs, follow-ups, broadcasts, human handoff, and customer communication workflows.",
      "offers": [
        { "@type": "Offer", "name": "Done-for-You WhatsApp Automation Setup", "price": "10850", "priceCurrency": "ZAR", "description": "Starting price, once-off. Chatfuel and WhatsApp messaging charges billed separately." },
        { "@type": "Offer", "name": "Done-with-You WhatsApp Automation Enablement", "price": "4850", "priceCurrency": "ZAR", "description": "Starting price, per month." },
      ],
    },
    {
      "@type": "FAQPage",
      "mainEntity": WA_FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://campaigns.rekisa.co.za/" },
        { "@type": "ListItem", "position": 2, "name": "WhatsApp Automation", "item": "https://campaigns.rekisa.co.za/whatsapp-automation" },
      ],
    },
  ],
};

export default function Whatsapp() {
  usePageMeta({
    title: "WhatsApp Automation South Africa | Chatbots & Lead Follow-Up | Rekisa",
    description: "Automate WhatsApp sales, support, lead capture, follow-ups and customer communication. Rekisa sets up WhatsApp automation using Chatfuel for South African businesses.",
    canonical: "https://campaigns.rekisa.co.za/whatsapp-automation",
    jsonLd: JSON_LD,
  });
  useHashScroll();

  const [selectedPlan, setSelectedPlan] = useState(null);

  const scrollMarks = useRef({ 50: false, 75: false, 90: false });
  const pricingViewed = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      [50, 75, 90].forEach((mark) => {
        if (pct >= mark && !scrollMarks.current[mark]) {
          scrollMarks.current[mark] = true;
          trackEvent(`scroll_${mark}`, { service: "whatsapp" });
        }
      });
      const inView = (id, ref, eventName) => {
        if (ref.current) return;
        const el = document.querySelector(`[data-testid=${id}]`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          ref.current = true;
          trackEvent(eventName, { service: "whatsapp" });
        }
      };
      inView("wa-pricing-section", pricingViewed, "pricing_section_view");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen bg-white text-zinc-900">
      <AuditAnnouncement service="whatsapp_automation" />
      <Header />
      <ReadingProgress
        sections={[
          { id: "wa-hero-section", label: "Top" },
          { id: "wa-problem-section", label: "Problems" },
          { id: "wa-demo-video-section", label: "Demo" },
          { id: "wa-automate-section", label: "Features" },
          { id: "wa-industries-section", label: "Use cases" },
          { id: "wa-process-section", label: "How it works" },
          { id: "wa-pricing-section", label: "Pricing" },
          { id: "wa-why-section", label: "Why Rekisa" },
          { id: "wa-faq-section", label: "FAQs" },
          { id: "wa-quote-form-section", label: "Quote" },
        ]}
      />
      <SectionRail
        service="whatsapp_automation"
        sections={[
          { id: "wa-hero-section", label: "Top" },
          { id: "wa-problem-section", label: "Problems" },
          { id: "wa-demo-video-section", label: "Demo" },
          { id: "wa-automate-section", label: "Features" },
          { id: "wa-industries-section", label: "Use cases" },
          { id: "wa-process-section", label: "How it works" },
          { id: "wa-pricing-section", label: "Pricing" },
          { id: "wa-why-section", label: "Why Rekisa" },
          { id: "wa-faq-section", label: "FAQs" },
          { id: "wa-quote-form-section", label: "Quote" },
        ]}
      />
      <main>
        <WhatsappHero />
        <WhatsappProblem />
        <WhatsappDemo />
        <WhatsappFeatures />
        <WhatsappIndustries />
        <WhatsappProcess />
        <WhatsappPricing setSelectedPlan={setSelectedPlan} />
        <WhatsappWhy />
        <WhatsappFAQ />
        <WhatsappForm selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} />
      </main>
      <MobileStickyCta label="Get a Quote" targetId="request-quote" heroId="top" service="whatsapp_automation" className="bg-[#25d366] text-[#04220f]" />
      <Footer />
      <AuditPopup service="whatsapp_automation" />
    </div>
  );
}
